import { Municipio, Uf } from 'app/modulos/venda/entrega/uniao';
import { ClienteAlergenoAcao, ClienteAlergenoPerfil } from './cliente-alergeno';
import { telefoneDigitos } from './telefone';
import {
  Endereco,
  enderecoMunicipioEntidade,
  enderecoUfEntidade,
} from 'app/modulos/venda/entrega/endereco';

// Cliente no servidor, lido de `aps://agente/cliente/{identificador}` e criado por
// `POST aps://agente/cliente`. A agenda (enderecos e cartoes) vem junto.
//
// `numero` e NUMBER aqui, enquanto o `Endereco` do front usa string; `uf` e `municipio`
// sao entidades, como no `pedidoEntrega`. As duas conversoes ficam nas funcoes abaixo,
// para a tela continuar falando so com `Endereco`.
export type AgenteClienteEndereco = {
  id: string;
  nome: string;
  cep: string;
  logradouro: string;
  numero: number;
  complemento: string;
  bairro: string;
  uf: Uf;
  municipio: Municipio;
  referencia: string;
};

export type AgenteCliente = {
  id: string;
  codigo: string;
  identificador: string;
  nome: string;
  email: string;
  telefone?: string;
  imagem: string;
  clienteEnderecos: AgenteClienteEndereco[];
  clienteCartoes: any[];
  // Perfil de alergenos; leitura em `cliente-alergeno.ts`.
  alergenos?: boolean;
  alergenosLista?: number[] | null;
  alergenosAcao?: number | null;
};

// Corpo do POST, que CRIA ou ATUALIZA pelo `identificador` (mesmo `id`, agenda preservada,
// campo omitido mantido). Resondado em 2026-10-28, exige: `nome` (>=1), `identificador`
// (>=10, sem espacos), `email` valido (<=120), `telefone` (>=8) E `alergenos` (boolean).
// O "email OU telefone" do CON-3683 nao vale mais no `sirius-s` - os dois sao cobrados.
export type AgenteClienteNovo = {
  nome: string;
  identificador: string;
  email?: string;
  telefone?: string;
  imagem?: string;
  alergenos?: boolean;
  alergenosLista?: number[];
  alergenosAcao?: number;
};

// Quem e o cliente para gravar: o que o servidor ja tem manda, o login completa. E-mail e
// telefone podem faltar nos dois (Google nao da telefone; o login por telefone nao da
// e-mail) - e a tela que pede.
export type AgenteClienteContato = {
  nome: string;
  identificador: string;
  email: string;
  telefone: string;
};

export function agenteClienteContato(
  agente: AgenteCliente,
  login: { nome?: string; identificador?: string; email?: string; telefone?: string },
): AgenteClienteContato | null {
  const identificador = agente?.identificador || login?.identificador || '';
  if (!identificador) {
    return null;
  }
  return {
    nome: agente?.nome || login?.nome || 'Cliente',
    identificador,
    email: agente?.email || login?.email || '',
    telefone: agente?.telefone || login?.telefone || '',
  };
}

// Mesma regra do `class-validator` do backend, de forma folgada: texto@dominio.tld, ate 120.
export function agenteEmailValido(email: string): boolean {
  const limpo = (email || '').trim();
  return limpo.length <= 120 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(limpo);
}

// Corpo do POST que grava o perfil de alergenos. Lista vazia ou acao Nenhuma desliga o
// perfil (`alergenos: false`) - o servidor guarda lista e acao como null, como num cliente
// que nunca cadastrou. Foto nao vai: omitida, o servidor mantem a que tem.
export function agenteClienteAlergenoCorpo(
  contato: AgenteClienteContato,
  perfil: ClienteAlergenoPerfil,
): AgenteClienteNovo {
  const ligado = !!perfil?.lista?.length && perfil.acao !== ClienteAlergenoAcao.Nenhuma;
  return {
    nome: contato.nome,
    identificador: contato.identificador,
    email: (contato.email || '').trim(),
    telefone: telefoneDigitos(contato.telefone),
    alergenos: ligado,
    alergenosLista: ligado ? [...perfil.lista] : [],
    alergenosAcao: ligado ? perfil.acao : ClienteAlergenoAcao.Nenhuma,
  };
}

// A `sigla` nao vem no `agente/cliente`, e e ela que `ENDERECO_OBRIGATORIOS` cobra: sem
// preencher, todo endereco do servidor seria julgado incompleto e travaria o checkout.
function siglaPorCodigo(ufs: Uf[], codigo: string): string {
  return (ufs || []).find(uf => uf && uf.codigo === codigo)?.sigla || '';
}

export function agenteEnderecoParaEndereco(origem: AgenteClienteEndereco, ufs: Uf[] = []): Endereco {
  const e = new Endereco();
  e.id = origem.id;
  e.apelido = origem.nome || '';
  e.cep = origem.cep || '';
  e.logradouro = origem.logradouro || '';
  e.numero = origem.numero != null ? String(origem.numero) : '';
  e.complemento = origem.complemento || '';
  e.bairro = origem.bairro || '';
  e.cidade = origem.municipio?.nome || '';
  // O `uf` do `agente/cliente` vem { id, codigo, nome } SEM `sigla`, e o `municipio` nao
  // traz o `uf` dentro. Para a tela (que mostra a sigla) e para o `pedidoEntrega` (que quer
  // o municipio com o uf aninhado), reconstroi o par a partir do que veio.
  const uf: Uf = origem.uf
    ? {
        codigo: origem.uf.codigo || '',
        nome: origem.uf.nome || '',
        sigla: (origem.uf as any).sigla || siglaPorCodigo(ufs, origem.uf.codigo),
      }
    : { codigo: '', nome: '', sigla: '' };
  e.uf = uf.sigla || '';
  e.pontoReferencia = origem.referencia || '';
  e.municipio = origem.municipio
    ? { codigo: origem.municipio.codigo || '', nome: origem.municipio.nome || '', uf }
    : null;
  return e;
}

export function agenteEnderecosParaAgenda(cliente: AgenteCliente, ufs: Uf[] = []): Endereco[] {
  return (cliente?.clienteEnderecos || []).map(e => agenteEnderecoParaEndereco(e, ufs));
}

// Corpo do POST/PUT de endereco. Exigidos: `cep`, `logradouro`, `bairro`, e `uf`/`municipio`
// como OBJETOS nao vazios.
//
// `uf` e `municipio` vao sem o `id` que aparece no doc do backend: nenhum endpoint que a
// loja alcanca devolve aquele uuid, e o backend confirmou que pode enviar sem. Nao tentar
// buscar o id - ele nao existe deste lado.
export type AgenteClienteEnderecoCorpo = {
  nome: string;
  cep: string;
  logradouro: string;
  numero?: number;
  complemento: string;
  bairro: string;
  uf: Uf;
  municipio: Municipio;
  referencia: string;
};

// O servidor exige `numero` INTEIRO (0..999999) e no front ele e texto livre ("S/N",
// "126A"). Sem digito nenhum o campo e OMITIDO, nao zerado: mandar 0 afirmaria um numero
// que o cliente nao deu. A letra de "126A" se perde - o servidor nao tem onde guardar.
export function agenteNumeroServidor(numero: string): number | undefined {
  const digitos = (numero || '').trim().match(/^\d+/);
  if (!digitos) {
    return undefined;
  }
  const valor = Number(digitos[0]);
  return valor > 999999 ? undefined : valor;
}

export function enderecoParaAgente(endereco: Endereco): AgenteClienteEnderecoCorpo {
  const corpo: AgenteClienteEnderecoCorpo = {
    nome: endereco?.apelido || 'Principal',
    cep: endereco?.cep || '',
    logradouro: endereco?.logradouro || '',
    complemento: endereco?.complemento || '',
    bairro: endereco?.bairro || '',
    uf: enderecoUfEntidade(endereco),
    municipio: enderecoMunicipioEntidade(endereco),
    referencia: endereco?.pontoReferencia || '',
  };
  const numero = agenteNumeroServidor(endereco?.numero);
  if (numero !== undefined) {
    corpo.numero = numero;
  }
  return corpo;
}
